import { Component, OnInit, Signal, ChangeDetectionStrategy } from '@angular/core';
import { GlobalEditStakeholderComponent } from 'src/app/shared/components/global-edit-stakeholder/global-edit-stakeholder.component';
import { LoadingSpinnerOverlayComponent } from 'src/app/shared/components/loading-spinner-overlay/loading-spinner-overlay.component';
import { RegistrationService } from '../../registration.service';
import { PublicStakeholderDTO } from 'src/app/features/subscription/types/PublicStakeholderDTO';

import { SubscriptionService } from 'src/app/features/subscription/subscription.service';
import { RegisterForMeetingRequestDTO } from '../../types/RegisterForMeetingRequestDTO';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { RegisterForSurveyRequestDTO } from '../../types/RegisterForSurveyRequestDTO';
import { PublicOnlineMeetingDTO } from '../../types/PublicOnlineMeetingDTO';
import { MinProjectDTO } from 'src/app/features/subscription/types/MinProjectDTO';
import { PublicMeetingDTO } from '../../types/PublicMeetingDTO';
import { LookupsDTO } from 'src/app/shared/types/LookupsDTO';

@Component({
  selector: 'app-new-stakeholder-registration',
  imports: [LoadingSpinnerOverlayComponent, GlobalEditStakeholderComponent],
  templateUrl: './new-stakeholder-registration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './new-stakeholder-registration.component.scss',
})
export class NewStakeholderRegistrationComponent implements OnInit {
  meetingInfo: PublicMeetingDTO | null = null;
  onlineMeetingInfo: PublicOnlineMeetingDTO | null = null;
  projectInfo: MinProjectDTO | null = null;
  stakeholderInfo: PublicStakeholderDTO = {} as PublicStakeholderDTO;
  isEdit: boolean = false;
  backUrl: string = '';
  registrationMode: string = '';
  projectName: string = '';
  meetingName: string = '';
  travelList: LookupsDTO[] = [];

  isLoadingEditStakeholder: Signal<boolean>;

  constructor(
    private registrationService: RegistrationService,
    private subscriptionService: SubscriptionService,
    private urlParamsService: UrlParamsService
  ) {
    this.isLoadingEditStakeholder = this.subscriptionService.isLoadingEditStakeholder$;
  }

  ngOnInit(): void {
    const {
      LastName,
      Mail1ZipCode,
      StakeEmail,
      isEdit,
      backUrl,
      registrationMode,
      SubscribeSourceId,
    } = history.state || {};

    if (!isEdit) {
      Object.assign(this.stakeholderInfo, {
        LastName,
        Mail1ZipCode,
        StakeEmail,
        SubscribeSourceId,
      });

      this.isEdit = isEdit;
      this.backUrl = backUrl;
      this.registrationMode = registrationMode;
    }

    this.projectInfo = this.registrationService.projectInfo$.value;

    if (this.registrationMode === 'in-person') {
      this.registrationService.meetingInfo$.subscribe({
        next: (meetingInfo) => {
          this.meetingInfo = meetingInfo;
          if (meetingInfo) {
            this.projectName = meetingInfo.ProjectName ?? '';
            this.meetingName = meetingInfo.MeetingName ?? '';
          }
        },
        error: (error) => console.error('Error fetching meeting info:', error),
      });
    } else if (this.registrationMode === 'online') {
      this.registrationService.onlineMeetingInfo$.subscribe({
        next: (meetingInfo) => {
          this.onlineMeetingInfo = meetingInfo;
          if (meetingInfo) {
            this.projectName = meetingInfo.ProjectName ?? '';
            this.meetingName = meetingInfo.MeetingName ?? '';
          }
        },
        error: (error) => console.error('Error fetching meeting info:', error),
      });
    } else if (this.registrationMode === 'survey123') {
      this.registrationService.projectInfo$.subscribe({
        next: (projectInfo) => {
          this.projectInfo = projectInfo;
          if (projectInfo) {
            this.projectName = projectInfo.ProjectName ?? '';
          }
        },
        error: (error) => console.error('Error fetching survey123 info:', error),
      });
    }
  }

  handleStakeholderEditCreateResult(event: { status: string; data: any }): void {
    if (event.status === 'error') {
      console.error('Error editing stakeholder:', event.data);
      return;
    }

    if (event.status !== 'success') {
      console.error('Unexpected status:', event.status);
      return;
    }

    const isSubscribed = event.data.IsReceiveEmailUpdate ?? false;

    if (this.registrationMode === 'survey123') {
      this.registerForSurvey(event.data.StakeGuid, isSubscribed);
    } else {
      this.registerForMeeting(event.data.StakeGuid, isSubscribed);
    }
  }

  private registerForSurvey(stakeGuid: string, isSubscribed: boolean): void {
    const registrant: RegisterForSurveyRequestDTO = {
      stakeGuid,
      projectGuid: this.urlParamsService.projectGuid || '',
      s123Id: this.urlParamsService.s123_id?.toString() || '',
    };

    this.registrationService.registerForSurvey(registrant).subscribe({
      next: (response) => {
        if (this.urlParamsService.projectGuid && stakeGuid) {
          this.triggerEmail(this.urlParamsService.projectGuid, stakeGuid, isSubscribed);
        }
        this.urlParamsService.navigateWithParams('/registration/survey123/confirmation', {
          survey123Url: response,
        });
      },
      error: (error) => {
        console.error('Error registering for survey:', error);
        this.urlParamsService.navigateToErrorPage('Error registering for survey.');
      },
    });
  }

  private registerForMeeting(stakeGuid: string, isSubscribed: boolean): void {
    let meetingGuid = '';
    if (this.registrationMode === 'online') {
      meetingGuid = this.onlineMeetingInfo?.MeetingGuid || '';
    } else if (this.registrationMode === 'in-person') {
      meetingGuid = this.meetingInfo?.MeetingGuid || '';
    }

    const registrant: RegisterForMeetingRequestDTO = {
      StakeGuid: stakeGuid,
      MeetingGuid: meetingGuid,
      IsOnline: this.registrationMode === 'online',
      IsAnonymous: false,
      TotalAttendees: 1,
    };

    this.registrationService.registerStakeholderForMeeting(registrant).subscribe({
      next: (response: { url?: string }) => {
        const confirmationPath =
          this.registrationMode === 'online'
            ? '/registration/online/confirmation'
            : '/registration/in-person/confirmation';

        const params =
          this.registrationMode === 'online' && response?.url
            ? { onlineMeetingUrl: response.url }
            : {};

        if (this.urlParamsService.projectGuid && stakeGuid) {
          this.triggerEmail(this.urlParamsService.projectGuid, stakeGuid, isSubscribed);
        }
        this.urlParamsService.navigateWithParams(confirmationPath, params);
      },
      error: (error) => {
        console.error('Error registering for meeting:', error);
        this.urlParamsService.navigateToErrorPage(
          'An error occurred while registering for the meeting.'
        );
      },
    });
  }

  triggerEmail(projectGuid: string, stakeGuid: string, isSubscribed: boolean = true) {
    this.registrationService
      .triggerConfirmationSubscriptionEmail(projectGuid, stakeGuid, isSubscribed)
      .subscribe({
        error: (error) => {
          console.error('Error sending confirmation email:', error);
        },
      });
  }
}
